import React, { createContext, useContext, useReducer } from 'react';

// Initial state
const initialState = {
  notices: [],
  loading: false,
  error: null,
  currentNotice: null
};

// Action types
const actionTypes = {
  FETCH_NOTICES_REQUEST: 'FETCH_NOTICES_REQUEST',
  FETCH_NOTICES_SUCCESS: 'FETCH_NOTICES_SUCCESS',
  FETCH_NOTICES_FAILURE: 'FETCH_NOTICES_FAILURE',
  FETCH_NOTICE_REQUEST: 'FETCH_NOTICE_REQUEST',
  FETCH_NOTICE_SUCCESS: 'FETCH_NOTICE_SUCCESS',
  FETCH_NOTICE_FAILURE: 'FETCH_NOTICE_FAILURE',
  CREATE_NOTICE_REQUEST: 'CREATE_NOTICE_REQUEST',
  CREATE_NOTICE_SUCCESS: 'CREATE_NOTICE_SUCCESS',
  CREATE_NOTICE_FAILURE: 'CREATE_NOTICE_FAILURE',
  UPDATE_NOTICE_REQUEST: 'UPDATE_NOTICE_REQUEST',
  UPDATE_NOTICE_SUCCESS: 'UPDATE_NOTICE_SUCCESS',
  UPDATE_NOTICE_FAILURE: 'UPDATE_NOTICE_FAILURE',
  DELETE_NOTICE_REQUEST: 'DELETE_NOTICE_REQUEST',
  DELETE_NOTICE_SUCCESS: 'DELETE_NOTICE_SUCCESS',
  DELETE_NOTICE_FAILURE: 'DELETE_NOTICE_FAILURE',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Reducer
const noticeReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.FETCH_NOTICES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case actionTypes.FETCH_NOTICES_SUCCESS:
      return {
        ...state,
        loading: false,
        notices: action.payload
      };
    case actionTypes.FETCH_NOTICES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case actionTypes.FETCH_NOTICE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case actionTypes.FETCH_NOTICE_SUCCESS:
      return {
        ...state,
        loading: false,
        currentNotice: action.payload
      };
    case actionTypes.FETCH_NOTICE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case actionTypes.CREATE_NOTICE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case actionTypes.CREATE_NOTICE_SUCCESS:
      return {
        ...state,
        loading: false,
        notices: [...state.notices, action.payload]
      };
    case actionTypes.CREATE_NOTICE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case actionTypes.UPDATE_NOTICE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case actionTypes.UPDATE_NOTICE_SUCCESS:
      return {
        ...state,
        loading: false,
        notices: state.notices.map(notice =>
          notice._id === action.payload._id ? action.payload : notice
        ),
        currentNotice: action.payload
      };
    case actionTypes.UPDATE_NOTICE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case actionTypes.DELETE_NOTICE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case actionTypes.DELETE_NOTICE_SUCCESS:
      return {
        ...state,
        loading: false,
        notices: state.notices.filter(notice => notice._id !== action.payload)
      };
    case actionTypes.DELETE_NOTICE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case actionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Context
const NoticeContext = createContext();

// Provider
export const NoticeProvider = ({ children }) => {
  const [state, dispatch] = useReducer(noticeReducer, initialState);

  // Actions
  const fetchNotices = async (params = {}) => {
    dispatch({ type: actionTypes.FETCH_NOTICES_REQUEST });
    try {
      // In a real app, this would be an API call
      // const response = await api.get('/notices', { params });
      // dispatch({ type: actionTypes.FETCH_NOTICES_SUCCESS, payload: response.data });
      
      // Mock data for now
      const mockNotices = [
        {
          _id: '1',
          title: 'University Annual Examination Schedule Released',
          description: 'The annual examination schedule for the current academic year has been officially released...',
          category: 'Exam',
          priority: 'Important',
          publishedBy: { name: 'Dr. Sarah Johnson' },
          createdAt: '2024-01-15T10:30:00Z',
          views: 1250
        },
        {
          _id: '2',
          title: 'New Library Hours During Exam Period',
          description: 'Library will remain open 24/7 during the examination period...',
          category: 'General',
          priority: 'Normal',
          publishedBy: { name: 'Library Dept' },
          createdAt: '2024-01-14T09:15:00Z',
          views: 890
        }
      ];
      dispatch({ type: actionTypes.FETCH_NOTICES_SUCCESS, payload: mockNotices });
    } catch (error) {
      dispatch({ type: actionTypes.FETCH_NOTICES_FAILURE, payload: error.message });
    }
  };

  const fetchNotice = async (id) => {
    dispatch({ type: actionTypes.FETCH_NOTICE_REQUEST });
    try {
      // In a real app, this would be an API call
      // const response = await api.get(`/notices/${id}`);
      // dispatch({ type: actionTypes.FETCH_NOTICE_SUCCESS, payload: response.data });
      
      // Mock data for now
      const mockNotice = {
        _id: id,
        title: 'University Annual Examination Schedule Released',
        description: 'The annual examination schedule for the current academic year has been officially released...',
        category: 'Exam',
        priority: 'Important',
        publishedBy: { name: 'Dr. Sarah Johnson' },
        createdAt: '2024-01-15T10:30:00Z',
        views: 1250
      };
      dispatch({ type: actionTypes.FETCH_NOTICE_SUCCESS, payload: mockNotice });
    } catch (error) {
      dispatch({ type: actionTypes.FETCH_NOTICE_FAILURE, payload: error.message });
    }
  };

  const createNotice = async (noticeData) => {
    dispatch({ type: actionTypes.CREATE_NOTICE_REQUEST });
    try {
      // In a real app, this would be an API call
      // const response = await api.post('/notices', noticeData);
      // dispatch({ type: actionTypes.CREATE_NOTICE_SUCCESS, payload: response.data });
      
      // Mock data for now
      const newNotice = {
        ...noticeData,
        _id: Math.random().toString(36).substr(2, 9),
        createdAt: new Date().toISOString(),
        views: 0
      };
      dispatch({ type: actionTypes.CREATE_NOTICE_SUCCESS, payload: newNotice });
    } catch (error) {
      dispatch({ type: actionTypes.CREATE_NOTICE_FAILURE, payload: error.message });
    }
  };

  const updateNotice = async (id, noticeData) => {
    dispatch({ type: actionTypes.UPDATE_NOTICE_REQUEST });
    try {
      // In a real app, this would be an API call
      // const response = await api.put(`/notices/${id}`, noticeData);
      // dispatch({ type: actionTypes.UPDATE_NOTICE_SUCCESS, payload: response.data });
      
      // Mock data for now
      const updatedNotice = {
        ...noticeData,
        _id: id,
        createdAt: new Date().toISOString()
      };
      dispatch({ type: actionTypes.UPDATE_NOTICE_SUCCESS, payload: updatedNotice });
    } catch (error) {
      dispatch({ type: actionTypes.UPDATE_NOTICE_FAILURE, payload: error.message });
    }
  };

  const deleteNotice = async (id) => {
    dispatch({ type: actionTypes.DELETE_NOTICE_REQUEST });
    try {
      // In a real app, this would be an API call
      // await api.delete(`/notices/${id}`);
      // dispatch({ type: actionTypes.DELETE_NOTICE_SUCCESS, payload: id });
      
      // Mock data for now
      dispatch({ type: actionTypes.DELETE_NOTICE_SUCCESS, payload: id });
    } catch (error) {
      dispatch({ type: actionTypes.DELETE_NOTICE_FAILURE, payload: error.message });
    }
  };

  const clearError = () => {
    dispatch({ type: actionTypes.CLEAR_ERROR });
  };

  const value = {
    ...state,
    fetchNotices,
    fetchNotice,
    createNotice,
    updateNotice,
    deleteNotice,
    clearError
  };

  return (
    <NoticeContext.Provider value={value}>
      {children}
    </NoticeContext.Provider>
  );
};

// Custom hook
export const useNotice = () => {
  const context = useContext(NoticeContext);
  if (!context) {
    throw new Error('useNotice must be used within a NoticeProvider');
  }
  return context;
};

export default NoticeContext;